import React, { useCallback, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { GraphChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { CabinetGraph } from "../../types/cabinet";
import { EDGE_TYPE_STYLES, NODE_KIND_STYLES } from "./cabinetTheme";
import { edgeTooltipHtml, nodeTooltipHtml } from "./cabinetTooltip";

// Tree-shaken registration (same pattern as EChartsBlock.tsx)
echarts.use([GraphChart, TooltipComponent, CanvasRenderer]);

interface CabinetGraphChartProps {
	graph: CabinetGraph;
	selectedNodeId: string | null;
	onSelectNode: (nodeId: string | null) => void;
}

interface EChartsClickParams {
	dataType?: string;
	data?: { id?: string };
}

/**
 * Force-directed view of the cabinet catalog.
 *
 * Node colour/size encode `kind`, edge stroke encodes `edge_type`. Clicking a
 * node lifts the selection to the page (which owns the side panel); clicking
 * empty canvas clears it. The legend lives outside this component in Tailwind —
 * ECharts cannot legend edge line styles, and one legend beats two.
 *
 * The chart instance is owned HERE, deliberately, rather than through the
 * React wrapper. The page polls every ten seconds, and the wrapper's default is
 * to re-render the whole chart from a new option object: with a force layout
 * that means the simulation restarts and every node jumps to a fresh random
 * seed, so a poll that changed nothing still visibly scrambles the picture the
 * user was reading. Owning the instance lets this component do the two things
 * that prevent it — merge by stable node `id` instead of replacing the series,
 * and skip `setOption` entirely when the option is unchanged.
 */
const CabinetGraphChart: React.FC<CabinetGraphChartProps> = ({
	graph,
	selectedNodeId,
	onSelectNode,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const chartRef = useRef<echarts.ECharts | null>(null);
	const lastOptionKey = useRef<string | null>(null);

	const option = useMemo(() => {
		const nodes = graph.nodes.map((node) => {
			const style = NODE_KIND_STYLES[node.kind];
			const isSelected = node.id === selectedNodeId;
			return {
				// STABLE across polls: ECharts matches series data by `id` when
				// merging, which is what lets an existing node keep its position
				// while a newly ingested one joins the simulation.
				id: node.id,
				name: node.label,
				symbolSize: style.symbolSize,
				value: node.kind,
				// Escaped in cabinetTooltip — ECharts renders this via innerHTML.
				tooltip: { formatter: nodeTooltipHtml(node) },
				itemStyle: {
					color: style.color,
					borderColor: isSelected ? "#111827" : "#FFFFFF",
					borderWidth: isSelected ? 3 : 1,
				},
				label: {
					show: true,
					position: "right" as const,
					fontSize: node.kind === "deal" ? 13 : 11,
					fontWeight: node.kind === "deal" ? ("600" as const) : ("400" as const),
					color: "#111827",
				},
			};
		});

		const links = graph.edges.map((edge) => {
			const style = EDGE_TYPE_STYLES[edge.type];
			return {
				source: edge.src,
				target: edge.dst,
				// Escaped in cabinetTooltip — `evidence` is classifier-derived too.
				tooltip: { formatter: edgeTooltipHtml(edge) },
				lineStyle: {
					color: style.color,
					type: style.type,
					width: edge.type === "belongs_to" ? 1.5 : 1,
					opacity: 0.85,
					curveness: edge.type === "references" ? 0.15 : 0,
				},
			};
		});

		return {
			tooltip: { confine: true },
			animationDuration: 600,
			series: [
				{
					type: "graph",
					layout: "force",
					roam: true,
					draggable: true,
					data: nodes,
					links,
					force: {
						repulsion: 320,
						edgeLength: [70, 180],
						gravity: 0.08,
						friction: 0.2,
					},
					emphasis: { focus: "adjacency", scale: false },
					labelLayout: { hideOverlap: true },
					lineStyle: { color: "source" },
				},
			],
		};
	}, [graph, selectedNodeId]);

	const handleClick = useCallback(
		(params: EChartsClickParams) => {
			if (params.dataType !== "node") return;
			const id = params.data?.id;
			if (!id) return;
			onSelectNode(id === selectedNodeId ? null : id);
		},
		[onSelectNode, selectedNodeId],
	);

	// Mount / unmount the instance. Deliberately empty deps: the chart outlives
	// every data change, which is the whole point.
	useEffect(() => {
		if (containerRef.current === null) return;
		const chart = echarts.init(containerRef.current);
		chartRef.current = chart;

		const resize = () => chart.resize();
		window.addEventListener("resize", resize);
		const observer =
			typeof ResizeObserver === "undefined"
				? null
				: new ResizeObserver(resize);
		observer?.observe(containerRef.current);

		return () => {
			window.removeEventListener("resize", resize);
			observer?.disconnect();
			chart.dispose();
			chartRef.current = null;
			lastOptionKey.current = null;
		};
	}, []);

	// Click handling is re-bound whenever the callback identity changes, so the
	// handler never closes over a stale selection.
	useEffect(() => {
		const chart = chartRef.current;
		if (chart === null) return;
		chart.off("click");
		chart.on("click", handleClick as (params: unknown) => void);
		return () => {
			chart.off("click");
		};
	}, [handleClick]);

	useEffect(() => {
		const chart = chartRef.current;
		if (chart === null) return;
		const key = JSON.stringify(option);
		// The churn guard. A ten-second poll that returns an identical catalog
		// must not touch the chart at all: setOption on a force layout restarts
		// the simulation, and doing that every ten seconds for no reason is
		// exactly the jitter this whole change exists to remove.
		if (key === lastOptionKey.current) return;
		lastOptionKey.current = key;
		// notMerge: false — merge into the existing series so ECharts can match
		// nodes by id and keep their positions.
		chart.setOption(option, { notMerge: false, lazyUpdate: true });
	}, [option]);

	return <div ref={containerRef} style={CHART_STYLE} data-testid="cabinet-chart" />;
};

// Extracted so the style object identity stays stable across renders.
const CHART_STYLE = { width: "100%", height: "100%" };

export default CabinetGraphChart;
